import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRepeat } from '@fortawesome/pro-light-svg-icons'

import {
  Box,
  Button,
  FileUploadZone,
  Form,
  Image,
  ModalContext,
  SidebarModal,
} from '@domain-group/suit-theme'

import useAttachment from '@hooks/useAttachment'

import { confirmDelete } from '@functions/entity'

import useBrandPublicLinkAttachments from './hooks/useBrandPublicLinkAttachments'

import LogoText from './components/LogoText'

const MODAL_KEY = 'ManageBrandPublicLinkAttachmentsModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageBrandPublicLinkAttachmentsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] || {}
  const { attachment: initAttachment, brand: initBrand } = modalPayload

  const { attachment_type, id: attachmentId } = initAttachment

  const attachmentPayload = useAttachment(initAttachment)
  const {
    callbacks: {
      deleteAttachment: deleteFn,
    },
  } = attachmentPayload

  const brandPublicLinkAttachmentsPayload = useBrandPublicLinkAttachments({
    brandId: initBrand.id,
    closeModal,
    initAttachment,
  })
  const {
    attachment,
    attachmentTypeString,
    attachmentUrl,
    callbacks: {
      setState,
    },
    showImage,
    uppy,
  } = brandPublicLinkAttachmentsPayload

  const confirmDeletePayload = {
    callbacks: {
      afterAction: () => setState({ showImage: false }),
      deleteFn,
    },
    options: {
      entityParams: attachment,
    },
    strings: {
      confirmString: 'Are you sure?',
      entityName: `${attachmentTypeString} Attachment`,
    },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={attachmentId ? 'Manage' : 'Upload'}
        titleSecondLine={attachmentTypeString}
      />
      <SidebarModal.Content>
        <Form>
          <LogoText attachment_type={attachment_type} />

          {!showImage && (
            <Form.Field label={`* Upload ${attachmentTypeString}`}>
              <FileUploadZone uppy={uppy} />
            </Form.Field>
          )}

          {showImage && (
            <>
              <Form.Field label={`Uploaded ${attachmentTypeString}`}>
                <Box justifyContent="center">
                  <Image
                    backgroundSize={attachment_type === 'public_link_logo' ? 'contain' : 'cover'}
                    borderRadius={5}
                    boxProps={{ border: '1px solid', borderColor: 'lineColor' }}
                    height={250}
                    style={{
                      backgroundColor: 'transparent',
                    }}
                    url={attachmentUrl}
                  />
                </Box>
              </Form.Field>

              <Form.Field label="More Options" marginTop="large">
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faRepeat} />}
                  onClick={() => {
                    confirmDelete(confirmDeletePayload)
                  }}
                  size="medium"
                >
                  Replace Image
                </Button>
              </Form.Field>
            </>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          onClick={() => closeModal()}
          size="large"
        >
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageBrandPublicLinkAttachmentsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
